export interface ICD10Code {
  code: string;
  description: string;
  clinical_disease?: string;
}

// Default ICD-10 codes per disease — used as fallback when LLM doesn't return them,
// and as suggestion list during approval editing. Keys are lowercase for matching.
export const DISEASE_ICD10_MAP: Record<string, ICD10Code[]> = {
  'hypertension': [
    { code: 'I10', description: 'Essential (primary) hypertension' },
  ],
  'hypertensive heart disease': [
    { code: 'I11.9', description: 'Hypertensive heart disease without heart failure' },
  ],
  'hypertensive': [
    { code: 'I10', description: 'Essential (primary) hypertension' },
  ],
  'mixed hyperlipidemia': [
    { code: 'E78.2', description: 'Mixed hyperlipidemia' },
  ],
  'hyperlipidemia unspecified': [
    { code: 'E78.5', description: 'Hyperlipidemia, unspecified' },
  ],
  'hyperlipidemia': [
    { code: 'E78.5', description: 'Hyperlipidemia, unspecified' },
    { code: 'E78.2', description: 'Mixed hyperlipidemia' },
  ],
  'dyslipidemia': [
    { code: 'E78.5', description: 'Hyperlipidemia, unspecified' },
    { code: 'E78.2', description: 'Mixed hyperlipidemia' },
  ],
  'hypercholesterolemia': [
    { code: 'E78.0', description: 'Pure hypercholesterolemia, unspecified' },
  ],
  'diabetes': [
    { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
  ],
  'diabetic': [
    { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
  ],
  'diabetes with hyperglycemia': [
    { code: 'E11.65', description: 'Type 2 diabetes mellitus with hyperglycemia' },
  ],
  'diabetes without complications': [
    { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
  ],
  'type 2 diabetes mellitus': [
    { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
    { code: 'E11.65', description: 'Type 2 diabetes mellitus with hyperglycemia' },
  ],
  'type 2 diabetes': [
    { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
    { code: 'E11.65', description: 'Type 2 diabetes mellitus with hyperglycemia' },
  ],
  'copd': [
    { code: 'J44.9', description: 'Chronic obstructive pulmonary disease, unspecified' },
  ],
  'chronic obstructive pulmonary disease': [
    { code: 'J44.9', description: 'Chronic obstructive pulmonary disease, unspecified' },
  ],
  'coronary artery disease': [
    { code: 'I25.10', description: 'Atherosclerotic heart disease of native coronary artery' },
  ],
  'atherosclerotic heart disease': [
    { code: 'I25.10', description: 'Atherosclerotic heart disease of native coronary artery' },
  ],
  'heart failure': [
    { code: 'I50.9', description: 'Heart failure, unspecified' },
  ],
  'congestive heart failure': [
    { code: 'I50.9', description: 'Heart failure, unspecified' },
  ],
  'atrial fibrillation': [
    { code: 'I48.91', description: 'Unspecified atrial fibrillation' },
  ],
  'afib': [
    { code: 'I48.91', description: 'Unspecified atrial fibrillation' },
  ],
  'chf': [
    { code: 'I50.9', description: 'Heart failure, unspecified' },
  ],
  'obesity': [
    { code: 'E66.9', description: 'Obesity, unspecified' },
  ],
  'osteoarthritis': [
    { code: 'M19.90', description: 'Unspecified osteoarthritis, unspecified site' },
  ],
  'osteoarthritic': [
    { code: 'M19.90', description: 'Unspecified osteoarthritis, unspecified site' },
  ],
  'asthma': [
    { code: 'J45.909', description: 'Unspecified asthma, uncomplicated' },
  ],
  'obstructive sleep apnea': [
    { code: 'G47.33', description: 'Obstructive sleep apnea' },
  ],
  'sleep apnea': [
    { code: 'G47.33', description: 'Obstructive sleep apnea' },
  ],
  'gout': [
    { code: 'M10.9', description: 'Gout, unspecified' },
  ],
  'gastroesophageal reflux': [
    { code: 'K21.9', description: 'Gastro-esophageal reflux disease without esophagitis' },
  ],
  'gerd': [
    { code: 'K21.9', description: 'Gastro-esophageal reflux disease without esophagitis' },
  ],
  'depression': [
    { code: 'F32.9', description: 'Major depressive disorder, single episode, unspecified' },
  ],
  'anxiety': [
    { code: 'F41.1', description: 'Generalized anxiety disorder' },
  ],
  'hypothyroidism': [
    { code: 'E03.9', description: 'Hypothyroidism, unspecified' },
  ],
  'benign prostatic hyperplasia': [
    { code: 'N40.1', description: 'Benign prostatic hyperplasia with lower urinary tract symptoms' },
  ],
  'bph': [
    { code: 'N40.1', description: 'Benign prostatic hyperplasia with lower urinary tract symptoms' },
  ],
  'enlarged prostate': [
    { code: 'N40.1', description: 'Benign prostatic hyperplasia with lower urinary tract symptoms' },
  ],
  'osteoporosis': [
    { code: 'M81.0', description: 'Age-related osteoporosis without current pathological fracture' },
  ],
  'vitamin d deficiency': [
    { code: 'E55.9', description: 'Vitamin D deficiency, unspecified' },
  ],
  'cataract': [
    { code: 'H25.9', description: 'Unspecified age-related cataract' },
  ],
  "alzheimer's disease": [
    { code: 'G30.9', description: "Alzheimer's disease, unspecified" },
  ],
  'alzheimer': [
    { code: 'G30.9', description: "Alzheimer's disease, unspecified" },
  ],
  "parkinson's disease": [
    { code: 'G20.A1', description: "Parkinson's disease without dyskinesia, without mention of fluctuations" },
  ],
  'parkinson': [
    { code: 'G20.A1', description: "Parkinson's disease without dyskinesia, without mention of fluctuations" },
  ],
  'dementia': [
    { code: 'F03.90', description: 'Unspecified dementia, unspecified severity, without behavioral disturbance, psychotic disturbance, mood disturbance, and anxiety' },
  ],
  'ischemic heart disease': [
    { code: 'I25.10', description: 'Atherosclerotic heart disease of native coronary artery without angina pectoris' },
  ],
  'ihd': [
    { code: 'I25.10', description: 'Atherosclerotic heart disease of native coronary artery without angina pectoris' },
  ],
  'cerebrovascular accident': [
    { code: 'I63.9', description: 'Cerebral infarction, unspecified' },
  ],
  'stroke': [
    { code: 'I63.9', description: 'Cerebral infarction, unspecified' },
  ],
  'tia': [
    { code: 'G45.9', description: 'Transient cerebral ischemic attack, unspecified' },
  ],
  // PHA-1219: plain "Anemia" is its own key so it is suggested D64.9, not the
  // iron-deficiency D50.9 that 'iron deficiency anemia' would otherwise supply.
  'anemia': [
    { code: 'D64.9', description: 'Anemia, unspecified' },
    { code: 'D50.9', description: 'Iron deficiency anemia, unspecified' },
    { code: 'D63.1', description: 'Anemia in chronic kidney disease' },
    { code: 'D63.8', description: 'Anemia in other chronic diseases classified elsewhere' },
  ],
  'iron deficiency anemia': [
    { code: 'D50.9', description: 'Iron deficiency anemia, unspecified' },
  ],
  'colorectal cancer': [
    { code: 'C18.9', description: 'Malignant neoplasm of colon, unspecified' },
  ],
  'lung cancer': [
    { code: 'C34.90', description: 'Malignant neoplasm of unspecified part of unspecified bronchus or lung' },
  ],
  'prostate cancer': [
    { code: 'C61', description: 'Malignant neoplasm of prostate' },
  ],
  'breast cancer': [
    { code: 'C50.919', description: 'Malignant neoplasm of unspecified site of unspecified female breast' },
  ],
  'rheumatoid arthritis': [
    { code: 'M06.9', description: 'Rheumatoid arthritis, unspecified' },
  ],
  'chronic kidney disease': [
    { code: 'N18.9', description: 'Chronic kidney disease, unspecified' },
  ],
  'ckd': [
    { code: 'N18.9', description: 'Chronic kidney disease, unspecified' },
  ],
  'low back pain': [
    { code: 'M54.5', description: 'Low back pain' },
  ],
};

// Patterns for the diseases that require ICD-10 codes for care plan generation.
// PHA-1078: must cover every disease we have a canonical care plan template for
// (backend/backend/prompts/CCM/canonical_care_plans/), not just a subset.
const SUPPORTED_DISEASE_PATTERNS: RegExp[] = [
  /hypertens/i,
  /hyperlipidemia|dyslipidemia/i,
  /diabetes|diabetic/i,
  /\bchf\b|congestive heart failure|heart failure/i,
  /\bcopd\b|chronic obstructive pulmonary/i,
  /\bafib\b|atrial fib/i,
  /obesity/i,
  /osteoarthrit/i,
  /gerd|gastroesophageal reflux/i,
  /anxiety/i,
  /depress/i,
  /hypothyroid/i,
  /benign prostatic|\bbph\b|enlarged prostate/i,
];

// Human-readable list for UI copy — keep in sync with SUPPORTED_DISEASE_PATTERNS above.
export const ICD10_REQUIRED_DISEASES_TEXT =
  'Hypertension, Hyperlipidemia, Diabetes, CHF, COPD, Atrial Fibrillation, Obesity, Osteoarthritis, GERD, Anxiety, Depression, Hypothyroidism, and Benign Prostatic Hyperplasia';

/**
 * Returns true if the problem name matches one of the supported diseases that
 * require ICD-10 codes (see ICD10_REQUIRED_DISEASES_TEXT).
 */
export const isSupportedDisease = (problemName: string): boolean =>
  SUPPORTED_DISEASE_PATTERNS.some(pattern => pattern.test(problemName));

/**
 * Get suggested ICD-10 codes for a given problem name by fuzzy matching
 * against the DISEASE_ICD10_MAP keys.
 */
export const getSuggestedICD10Codes = (problemName: string): ICD10Code[] => {
  const lower = problemName.toLowerCase();
  // Try exact key match first
  if (DISEASE_ICD10_MAP[lower]) {
    return DISEASE_ICD10_MAP[lower];
  }
  // Find all matching keys, prefer the most specific (longest) match.
  // Abbreviation keys (tia, ckd, chf...) must match a whole word, or 'tia' would
  // code "Essential tremor" / "Partial seizure" as a TIA.
  const matchedKeys = Object.keys(DISEASE_ICD10_MAP).filter(k =>
    k.length <= 4
      ? new RegExp(`\\b${k}\\b`).test(lower)
      : lower.includes(k) || k.includes(lower)
  );
  if (matchedKeys.length === 0) return [];
  matchedKeys.sort((a, b) => b.length - a.length);
  return DISEASE_ICD10_MAP[matchedKeys[0]];
};
